import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Ruler, ChevronLeft, Building2, Check, DollarSign, Mail, Edit2, Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import { getSuppliers, Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import { createQuoteRequest, sendQuoteRequestEmailForMultipleItems, generateQuoteRequestEmailHTMLForMultipleItems } from '../lib/quoteRequests';
import { getProjectMeasurements, ProjectMeasurement, getCustomFieldValue, getCustomFieldUnit as getFieldUnit } from '../lib/projectMeasurements';
import { getMeasurementPresets, MeasurementPreset } from '../lib/measurementPresets';
import { getMeasurementPhotos, getMeasurementPhotoUrl, type MeasurementPhoto } from '../services/measurementPhotoService';
import { toast } from './toasts/SimpleToast';

interface RequestQuoteFromProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string | null;
  projectTitle?: string | null;
  onRequestQuote?: () => void;
  onRequestQuoteFromMeasurements?: () => void;
}

type ModalStep = 'options' | 'select-suppliers' | 'select-measurements' | 'item-details' | 'summary';

export default function RequestQuoteFromProjectModal({
  isOpen,
  onClose,
  projectId,
  projectTitle,
  onRequestQuote,
  onRequestQuoteFromMeasurements
}: RequestQuoteFromProjectModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentStep, setCurrentStep] = useState<ModalStep>('options');
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<Set<string>>(new Set());
  const [selectedSuppliers, setSelectedSuppliers] = useState<Customer[]>([]);
  interface QuoteItem {
    id: string;
    name: string;
    description: string;
    acquisition_cost: number;
    price: number;
    quantity: number;
    measurement_id?: string | null;
    measurement?: ProjectMeasurement | null;
  }
  
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [measurements, setMeasurements] = useState<ProjectMeasurement[]>([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState<string | null>(null);
  const [selectedMeasurement, setSelectedMeasurement] = useState<ProjectMeasurement | null>(null);
  const [measurementPhotos, setMeasurementPhotos] = useState<Record<string, MeasurementPhoto[]>>({});
  const [measurementPresets, setMeasurementPresets] = useState<MeasurementPreset[]>([]);
  const [loadingMeasurementPresets, setLoadingMeasurementPresets] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    acquisition_cost: '',
    price: '',
    quantity: '1'
  });
  const [notes, setNotes] = useState('');
  const [isQuoteFromMeasurements, setIsQuoteFromMeasurements] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const priceWasAutoFilledRef = useRef(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setCurrentStep('options');
      setSelectedSupplierIds(new Set());
      setSelectedSuppliers([]);
      setItems([]);
      setMeasurements([]);
      setSelectedMeasurementId(null);
      setSelectedMeasurement(null);
      setIsQuoteFromMeasurements(false);
      setFormData({
        name: '',
        description: '',
        acquisition_cost: '',
        price: '',
        quantity: '1'
      });
      setNotes('');
      priceWasAutoFilledRef.current = false;
      setError(null);
    }
  }, [isOpen]);

  // Load suppliers when entering supplier selection step
  const loadSuppliers = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoadingSuppliers(true);
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    } finally {
      setLoadingSuppliers(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (currentStep === 'select-suppliers' && suppliers.length === 0) {
      loadSuppliers();
    }
  }, [currentStep, suppliers.length, loadSuppliers]);

  // Load measurements when entering measurement selection step
  const loadMeasurements = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoadingMeasurements(true);
      const data = await getProjectMeasurements(projectId);
      setMeasurements(data);
      
      // Load photos for all measurements
      const photosMap: Record<string, MeasurementPhoto[]> = {};
      
      for (const measurement of data) {
        if (measurement.id) {
          try {
            const photos = await getMeasurementPhotos(measurement.id);
            photosMap[measurement.id] = photos;
          } catch (err) {
            console.error(`Error loading photos for measurement ${measurement.id}:`, err);
            photosMap[measurement.id] = [];
          }
        }
      }
      
      setMeasurementPhotos(photosMap);
    } catch (err) {
      console.error('Error loading measurements:', err);
    } finally {
      setLoadingMeasurements(false);
    }
  }, [projectId]);

  const loadMeasurementPresets = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    if (measurementPresets.length > 0) return;

    try {
      setLoadingMeasurementPresets(true);
      const presets = await getMeasurementPresets(selectedBusiness.id);
      setMeasurementPresets(presets || []);
    } catch (err) {
      console.error('Error loading measurement presets:', err);
    } finally {
      setLoadingMeasurementPresets(false);
    }
  }, [selectedBusiness?.id, measurementPresets.length]);

  useEffect(() => {
    if (currentStep === 'select-measurements' && measurements.length === 0 && projectId) {
      loadMeasurements();
    }
    if (currentStep === 'select-measurements' && selectedBusiness?.id) {
      loadMeasurementPresets();
    }
  }, [currentStep, measurements.length, projectId, loadMeasurements, loadMeasurementPresets, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleOptionClick = (option: 'quote' | 'quote-from-measurements') => {
    if (option === 'quote') {
      // Regular quote flow
      setIsQuoteFromMeasurements(false);
      setCurrentStep('select-suppliers');
    } else if (option === 'quote-from-measurements') {
      // Quote from measurements flow
      setIsQuoteFromMeasurements(true);
      setCurrentStep('select-suppliers');
    }
  };

  const handleSupplierToggle = (supplierId: string) => {
    setSelectedSupplierIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  };

  const handleBack = () => {
    if (currentStep === 'summary') {
      setCurrentStep('item-details');
    } else if (currentStep === 'item-details') {
      // If we came from measurements, go back to measurements, otherwise go to suppliers
      if (selectedMeasurementId) {
        setCurrentStep('select-measurements');
      } else {
        setCurrentStep('select-suppliers');
      }
    } else if (currentStep === 'select-measurements') {
      // If we're in measurements flow and have items, go back to item-details
      // Otherwise go back to suppliers
      if (isQuoteFromMeasurements && items.length > 0) {
        setCurrentStep('item-details');
      } else {
        setCurrentStep('select-suppliers');
        setSelectedMeasurementId(null);
        setSelectedMeasurement(null);
      }
    } else if (currentStep === 'select-suppliers') {
      setCurrentStep('options');
      setSelectedSupplierIds(new Set());
      setSelectedSuppliers([]);
    }
  };

  const handleSelectAll = () => {
    if (selectedSupplierIds.size === suppliers.length) {
      // Deselect all
      setSelectedSupplierIds(new Set());
    } else {
      // Select all
      setSelectedSupplierIds(new Set(suppliers.map(s => s.id)));
    }
  };

  const handleNextToMeasurements = () => {
    if (selectedSupplierIds.size === 0) return;
    
    // Get selected supplier objects
    const selected = suppliers.filter(s => selectedSupplierIds.has(s.id));
    setSelectedSuppliers(selected);
    setCurrentStep('select-measurements');
  };

  const handleNextToItemDetails = () => {
    if (selectedSupplierIds.size === 0) return;
    
    // Get selected supplier objects (if not already set)
    if (selectedSuppliers.length === 0) {
      const selected = suppliers.filter(s => selectedSupplierIds.has(s.id));
      setSelectedSuppliers(selected);
    }
    setCurrentStep('item-details');
  };

  const handleMeasurementSelect = (measurementId: string) => {
    setSelectedMeasurementId(measurementId);
    const measurement = measurements.find(m => m.id === measurementId);
    setSelectedMeasurement(measurement || null);
    // Auto-populate item name from measurement name
    if (measurement) {
      setFormData(prev => ({
        ...prev,
        name: measurement.name || ''
      }));
    }
    setCurrentStep('item-details');
  };

  const normalizeFieldKey = (value: string) =>
    value.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

  const findMatchingPreset = (measurement: ProjectMeasurement): MeasurementPreset | null => {
    if (!measurement || measurementPresets.length === 0) return null;

    const measurementFields = new Set<string>();
    if (measurement.length !== null && measurement.length !== undefined) measurementFields.add('length');
    if (measurement.width !== null && measurement.width !== undefined) measurementFields.add('width');
    if (measurement.height !== null && measurement.height !== undefined) measurementFields.add('height');
    if (measurement.area !== null && measurement.area !== undefined) measurementFields.add('area');

    if (measurement.custom_fields) {
      Object.entries(measurement.custom_fields).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          measurementFields.add(normalizeFieldKey(key));
        }
      });
    }

    for (const preset of measurementPresets) {
      if (preset.custom_fields && preset.custom_fields.length > 0) {
        const presetFieldsSet = new Set(
          preset.custom_fields
            .filter(f => f.name !== 'area' || !f.calculate)
            .map(f => normalizeFieldKey(f.name))
        );

        const presetFieldsArray = Array.from(presetFieldsSet);
        const hasAllPresetFields = presetFieldsArray.length > 0 &&
          presetFieldsArray.every(field => measurementFields.has(field));

        const measurementFieldsArray = Array.from(measurementFields);
        const hasNoExtraFields = measurementFieldsArray.every(field =>
          presetFieldsSet.has(field) || field === 'area'
        );

        if (hasAllPresetFields && hasNoExtraFields) {
          return preset;
        }
      } else {
        const presetFields = new Set<string>();
        if (preset.has_length) presetFields.add('length');
        if (preset.has_width) presetFields.add('width');
        if (preset.has_height) presetFields.add('height');
        if (preset.has_area) presetFields.add('area');

        const presetFieldsArray = Array.from(presetFields);
        const hasAllPresetFields = presetFieldsArray.length > 0 &&
          presetFieldsArray.every(field => measurementFields.has(field));

        if (hasAllPresetFields) {
          return preset;
        }
      }
    }

    return null;
  };

  const getStandardFieldUnit = (measurement: ProjectMeasurement, fieldName: string) => {
    const preset = findMatchingPreset(measurement);
    if (preset?.custom_fields && preset.custom_fields.length > 0) {
      const presetField = preset.custom_fields.find(f => normalizeFieldKey(f.name) === normalizeFieldKey(fieldName));
      if (presetField?.unit) return presetField.unit;
    }
    return measurement.unit;
  };

  const getCustomFieldUnit = (measurement: ProjectMeasurement, fieldKey: string) => {
    const preset = findMatchingPreset(measurement);
    if (!preset?.custom_fields || preset.custom_fields.length === 0) return undefined;

    const normalizedKey = normalizeFieldKey(fieldKey);
    const presetField = preset.custom_fields.find(field => {
      const normalizedName = normalizeFieldKey(field.name);
      const normalizedLabel = normalizeFieldKey(field.label || '');
      return normalizedName === normalizedKey || normalizedLabel === normalizedKey;
    });
    return presetField?.unit;
  };

  const buildMeasurementFieldUnits = (measurement: ProjectMeasurement) => {
    const fieldUnits: Record<string, string> = {};
    const standardFields: Array<'length' | 'width' | 'height' | 'area'> = ['length', 'width', 'height', 'area'];
    standardFields.forEach(field => {
      if (measurement[field] !== null && measurement[field] !== undefined) {
        fieldUnits[field] = getStandardFieldUnit(measurement, field);
      }
    });
    if (measurement.custom_fields) {
      Object.keys(measurement.custom_fields).forEach(key => {
        const unit = getCustomFieldUnit(measurement, key);
        if (unit) {
          fieldUnits[key] = unit;
        }
      });
    }
    return Object.keys(fieldUnits).length > 0 ? fieldUnits : undefined;
  };

  const resolveMeasurementForItem = (item: { measurement_id?: string | null; measurement?: ProjectMeasurement | null }) => {
    if (item.measurement_id) {
      const latest = measurements.find(m => m.id === item.measurement_id);
      if (latest) {
        return latest;
      }
    }
    return item.measurement || null;
  };

  const handleBackToMeasurements = () => {
    setCurrentStep('select-measurements');
  };

  const handleSaveItem = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!formData.name.trim()) {
      setError('Item name is required');
      return;
    }

    const acquisitionCost = parseFloat(formData.acquisition_cost) || 0;
    if (isNaN(acquisitionCost) || acquisitionCost < 0) {
      setError('Please enter a valid item cost (can be 0)');
      return;
    }

    const price = parseFloat(formData.price) || 0;
    if (isNaN(price) || price < 0) {
      setError('Please enter a valid retail price (can be 0)');
      return;
    }

    const quantity = parseInt(formData.quantity) || 1;
    if (isNaN(quantity) || quantity <= 0) {
      setError('Please enter a valid quantity (greater than 0)');
      return;
    }

    // Add item to the list
    const newItem: QuoteItem = {
      id: `item-${Date.now()}-${Math.random()}`,
      name: formData.name.trim(),
      description: formData.description.trim(),
      acquisition_cost: acquisitionCost,
      price: price,
      quantity: quantity,
      measurement_id: selectedMeasurementId || null,
      measurement: selectedMeasurement || null
    };

    setItems(prev => [...prev, newItem]);
    setError(null);
    
    // Clear the selected measurement and form after saving item
    // User needs to select a new measurement to create another item
    setSelectedMeasurementId(null);
    setSelectedMeasurement(null);
    setFormData({
      name: '',
      description: '',
      acquisition_cost: '',
      price: '',
      quantity: '1'
    });
    priceWasAutoFilledRef.current = false;
    
    // Stay on item-details step - the form will be hidden and "Add Item" button will show
    // User can either add another item or continue to summary
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleContinueToSummary = () => {
    if (items.length === 0) {
      setError('Please add at least one item');
      return;
    }
    setError(null);
    setCurrentStep('summary');
  };

  const handleSendQuoteRequests = async () => {
    if (!selectedBusiness?.id) {
      setError('No business selected');
      return;
    }

    if (items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get business name for email
      const businessName = selectedBusiness.name || 'Our Business';

      // Create quote request and send email for each selected supplier
      const results = await Promise.allSettled(
        selectedSuppliers.map(async (supplier) => {
          // Generate a unique group ID for this batch of items sent to this supplier
          const groupId = crypto.randomUUID();
          
          // Create one quote request per item for this supplier, all with the same group_id
          const quoteRequests = await Promise.all(
            items.map(item =>
              createQuoteRequest({
                business_id: selectedBusiness.id,
                project_id: projectId || null,
                supplier_id: supplier.id,
                item_name: item.name,
                description: item.description || null,
                estimated_quantity: item.quantity,
                estimated_cost: item.acquisition_cost,
                retail_price: item.price,
                unit: 'unit',
                notes: notes.trim() || null,
                measurement_id: item.measurement_id || null,
                quote_request_group_id: groupId
              })
            )
          );

          // Send one email to supplier with all items
          if (supplier.email) {
            const emailResult = await sendQuoteRequestEmailForMultipleItems(
              selectedBusiness.id,
              supplier.email,
              `${supplier.first_name} ${supplier.last_name}`.trim(),
              businessName,
              projectTitle || null,
              items.map(item => {
                const measurement = resolveMeasurementForItem(item);
                return ({
                name: item.name,
                description: item.description || null,
                quantity: item.quantity,
                estimatedCost: item.acquisition_cost,
                retailPrice: item.price,
                unit: 'unit',
                supplierId: supplier.id, // Pass supplier ID for email linking
                measurement: measurement ? {
                  name: measurement.name,
                  length: measurement.length || null,
                  width: measurement.width || null,
                  height: measurement.height || null,
                  area: measurement.area || null,
                  unit: measurement.unit,
                  custom_fields: measurement.custom_fields || null,
                  field_units: buildMeasurementFieldUnits(measurement) || null,
                  photos: item.measurement_id && measurementPhotos[item.measurement_id] 
                    ? measurementPhotos[item.measurement_id].map(photo => ({
                        file_path: photo.file_path,
                        file_name: photo.file_name
                      }))
                    : null
                } : null
              });
              }),
              notes.trim() || null,
              groupId // Pass the group ID to link the email
            );

            if (!emailResult.success) {
              console.error(`Failed to send email to ${supplier.email}:`, emailResult.error);
            }
          }

          return { supplier, quoteRequests, success: true };
        })
      );

      // Check results
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      const totalQuoteRequests = results
        .filter(r => r.status === 'fulfilled')
        .reduce((sum, r) => sum + (r.value as any).quoteRequests.length, 0);

      if (successful > 0) {
        toast.success({
          title: 'Quote Requests Sent',
          description: `Successfully sent ${totalQuoteRequests} quote request${totalQuoteRequests > 1 ? 's' : ''} to ${successful} supplier${successful > 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}`
        });
        onClose();
        if (onRequestQuote) {
          onRequestQuote();
        }
      } else {
        setError('Failed to send quote requests. Please try again.');
      }
    } catch (err) {
      console.error('Error creating quote requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to create quote requests');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100030,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100040
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            {(currentStep === 'select-suppliers' || currentStep === 'item-details' || currentStep === 'summary' || currentStep === 'select-measurements') && 
             !(currentStep === 'item-details' && isQuoteFromMeasurements && items.length > 0 && !selectedMeasurementId) && (
              <button
                onClick={handleBack}
                className="absolute left-4 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              {currentStep === 'options' 
                ? 'Request Quote' 
                : currentStep === 'select-suppliers'
                ? 'Select Suppliers'
                : currentStep === 'item-details'
                ? 'Item Details'
                : 'Review & Send'
              }
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {currentStep === 'options' 
                ? 'Choose how you want to request a quote'
                : currentStep === 'select-suppliers'
                ? 'Select one or more suppliers to request quotes from'
                : currentStep === 'item-details'
                ? 'Enter details for the item you need a quote for'
                : 'Review the quote request before sending'
              }
            </p>
          </div>

          {/* Content */}
          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            {currentStep === 'options' ? (
              <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-4`}>
                {/* Request Quote */}
                <button
                  onClick={() => handleOptionClick('quote')}
                  className="w-full p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 transition-all duration-150 active:scale-[0.98] text-left group"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        Request Quote
                      </h3>
                      <p className="text-sm text-gray-500">
                        Request a quote for supplies needed for this project
                      </p>
                    </div>
                  </div>
                </button>

                {/* Request Quote From Measurements */}
                <button
                  onClick={() => handleOptionClick('quote-from-measurements')}
                  className="w-full p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 transition-all duration-150 active:scale-[0.98] text-left group"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <Ruler className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        Request Quote From Measurements
                      </h3>
                      <p className="text-sm text-gray-500">
                        Request a quote based on project measurements
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            ) : currentStep === 'select-suppliers' ? (
              <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
                {loadingSuppliers ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
                  </div>
                ) : suppliers.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No suppliers found</p>
                    <p className="text-xs text-gray-400 mt-1">Add suppliers to request quotes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Select All Button */}
                    {suppliers.length > 0 && (
                      <button
                        onClick={handleSelectAll}
                        className="w-full px-4 py-2.5 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg text-sm font-medium text-gray-700 transition-colors duration-150 active:scale-[0.98]"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {selectedSupplierIds.size === suppliers.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                    
                    {suppliers.map((supplier) => {
                      const isSelected = selectedSupplierIds.has(supplier.id);
                      return (
                        <button
                          key={supplier.id}
                          onClick={() => handleSupplierToggle(supplier.id)}
                          className={`w-full p-4 bg-white rounded-xl border-2 transition-all duration-150 active:scale-[0.98] text-left ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                              isSelected ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                            }`}>
                              {supplier.first_name?.[0]}{supplier.last_name?.[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {supplier.first_name} {supplier.last_name}
                              </div>
                              {supplier.email && (
                                <div className="text-xs text-gray-500 truncate">
                                  {supplier.email}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <div className="flex-shrink-0">
                                <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                  <Check className="h-4 w-4 text-white" />
                                </div>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {/* Next Button */}
                {!loadingSuppliers && suppliers.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={isQuoteFromMeasurements ? handleNextToMeasurements : handleNextToItemDetails}
                      disabled={selectedSupplierIds.size === 0}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors duration-150 active:scale-[0.98]"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Next {selectedSupplierIds.size > 0 && `(${selectedSupplierIds.size} selected)`}
                    </button>
                  </div>
                )}
              </div>
            ) : currentStep === 'select-measurements' ? (
              <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
                {loadingMeasurements ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
                  </div>
                ) : measurements.length === 0 ? (
                  <div className="text-center py-12">
                    <Ruler className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No measurements found</p>
                    <p className="text-xs text-gray-400 mt-1">Add measurements to this project first</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {measurements.map((measurement) => {
                      // Check if this measurement already has an item linked to it
                      const linkedItems = items.filter(item => item.measurement_id === measurement.id);
                      const isAlreadyUsed = linkedItems.length > 0;
                      
                      return (
                        <button
                          key={measurement.id}
                          onClick={() => !isAlreadyUsed && handleMeasurementSelect(measurement.id)}
                          disabled={isAlreadyUsed}
                          className={`w-full p-4 bg-white rounded-xl border-2 transition-all duration-150 text-left ${
                            isAlreadyUsed
                              ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                              : selectedMeasurementId === measurement.id
                              ? 'border-blue-500 bg-blue-50 active:scale-[0.98]'
                              : 'border-gray-200 hover:border-gray-300 active:scale-[0.98]'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                              isAlreadyUsed 
                                ? 'bg-gray-200' 
                                : selectedMeasurementId === measurement.id 
                                ? 'bg-blue-600' 
                                : 'bg-gray-100'
                            }`}>
                              <Ruler className={`h-5 w-5 ${
                                isAlreadyUsed 
                                  ? 'text-gray-400' 
                                  : selectedMeasurementId === measurement.id 
                                  ? 'text-white' 
                                  : 'text-gray-600'
                              }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <div className={`text-sm font-semibold ${
                                  isAlreadyUsed ? 'text-gray-500' : 'text-gray-900'
                                }`}>
                                  {measurement.name}
                                </div>
                                {isAlreadyUsed && (
                                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full flex-shrink-0">
                                    Used
                                  </span>
                                )}
                              </div>
                              {(measurement.length || measurement.width || measurement.height || measurement.area) && (
                                <div className={`flex flex-wrap gap-2 mb-2 text-xs ${
                                  isAlreadyUsed ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  {measurement.length && <span>L: {measurement.length} {getStandardFieldUnit(measurement, 'length')}</span>}
                                  {measurement.width && <span>W: {measurement.width} {getStandardFieldUnit(measurement, 'width')}</span>}
                                  {measurement.height && <span>H: {measurement.height} {getStandardFieldUnit(measurement, 'height')}</span>}
                                  {measurement.area && <span>Area: {measurement.area} {getStandardFieldUnit(measurement, 'area')}²</span>}
                                </div>
                              )}
                              {measurement.custom_fields && Object.keys(measurement.custom_fields).length > 0 && (
                                <div className={`flex flex-wrap gap-2 mb-2 text-xs ${
                                  isAlreadyUsed ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  {Object.entries(measurement.custom_fields).map(([key, value]) => {
                                    const displayValue = getCustomFieldValue(value);
                                    const unit = getFieldUnit(value, getCustomFieldUnit(measurement, key));
                                    return (
                                      <span key={key}>
                                        {key}: {displayValue}{unit ? ` ${unit}` : ''}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                              {measurement.notes && (
                                <div className={`text-xs mt-2 pt-2 border-t border-gray-200/60 ${
                                  isAlreadyUsed ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  <span className="font-medium">Notes: </span>
                                  {measurement.notes}
                                </div>
                              )}
                              {/* Measurement Photos */}
                              {measurement.id && measurementPhotos[measurement.id] && measurementPhotos[measurement.id].length > 0 && (
                                <div className="mt-2 pt-2 border-t border-gray-200/60">
                                  <div className="flex items-center gap-1 mb-1">
                                    <ImageIcon className="h-2.5 w-2.5 text-gray-500" />
                                    <span className="text-[10px] font-medium text-gray-600">
                                      Photos ({measurementPhotos[measurement.id].length})
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {measurementPhotos[measurement.id].slice(0, 3).map((photo) => {
                                      const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                                      return (
                                        <div
                                          key={photo.id}
                                          className="relative w-12 h-12 rounded overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0"
                                        >
                                          <img
                                            src={photoUrl}
                                            alt={photo.file_name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                    {measurementPhotos[measurement.id].length > 3 && (
                                      <span className="text-[10px] text-gray-500 font-medium">
                                        .....
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {isAlreadyUsed && (
                                <div className="mt-2 text-xs text-gray-500">
                                  Linked to: {linkedItems.map(item => item.name).join(', ')}
                                </div>
                              )}
                            </div>
                            {selectedMeasurementId === measurement.id && !isAlreadyUsed && (
                              <div className="flex-shrink-0">
                                <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                  <Check className="h-4 w-4 text-white" />
                                </div>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : currentStep === 'item-details' ? (
              <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4">
                    {error}
                  </div>
                )}

                {/* Selected Measurement Info - Compact Header */}
                {selectedMeasurement && (
                  <div className="mb-5 pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Ruler className="h-4 w-4 text-blue-600" />
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">From Measurement</span>
                      </div>
                      <button
                        onClick={handleBackToMeasurements}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                      >
                        Change
                      </button>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">{selectedMeasurement.name}</div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-600">
                          {selectedMeasurement.length && <span>L: {selectedMeasurement.length} {getStandardFieldUnit(selectedMeasurement, 'length')}</span>}
                          {selectedMeasurement.width && <span>W: {selectedMeasurement.width} {getStandardFieldUnit(selectedMeasurement, 'width')}</span>}
                          {selectedMeasurement.height && <span>H: {selectedMeasurement.height} {getStandardFieldUnit(selectedMeasurement, 'height')}</span>}
                          {selectedMeasurement.area && <span>Area: {selectedMeasurement.area} {getStandardFieldUnit(selectedMeasurement, 'area')}²</span>}
                          {selectedMeasurement.custom_fields && Object.keys(selectedMeasurement.custom_fields).length > 0 && (
                            <>
                              {Object.entries(selectedMeasurement.custom_fields).map(([key, value]) => (
                                <span key={key}>
                                  {key}: {value}{getCustomFieldUnit(selectedMeasurement, key) ? ` ${getCustomFieldUnit(selectedMeasurement, key)}` : ''}
                                </span>
                              ))}
                            </>
                          )}
                        </div>
                        {selectedMeasurement.notes && (
                          <div className="mt-2 text-xs text-gray-500 italic">{selectedMeasurement.notes}</div>
                        )}
                        {/* Measurement Photos */}
                        {selectedMeasurement.id && measurementPhotos[selectedMeasurement.id] && measurementPhotos[selectedMeasurement.id].length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200/60">
                            <div className="flex items-center gap-1 mb-1">
                              <ImageIcon className="h-2.5 w-2.5 text-gray-500" />
                              <span className="text-[10px] font-medium text-gray-600">
                                Photos ({measurementPhotos[selectedMeasurement.id].length})
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              {measurementPhotos[selectedMeasurement.id].slice(0, 3).map((photo) => {
                                const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                                return (
                                  <div
                                    key={photo.id}
                                    className="relative w-12 h-12 rounded overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0"
                                  >
                                    <img
                                      src={photoUrl}
                                      alt={photo.file_name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                                      }}
                                    />
                                  </div>
                                );
                              })}
                              {measurementPhotos[selectedMeasurement.id].length > 3 && (
                                <span className="text-[10px] text-gray-500 font-medium">
                                  .....
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Saved Items List */}
                {items.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-gray-900">Saved Items</h3>
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">{items.length}</span>
                    </div>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {items.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                          {/* Item Header */}
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <h4 className="text-sm font-semibold text-gray-900">{item.name}</h4>
                                </div>
                                {item.description && (
                                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-gray-500">Qty:</span>
                                    <span className="font-medium text-gray-700">{item.quantity}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-gray-500">Cost:</span>
                                    <span className="font-medium text-gray-700">
                                      {item.acquisition_cost > 0 ? `$${item.acquisition_cost.toFixed(2)}` : 'TBD'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-gray-500">Price:</span>
                                    <span className="font-medium text-gray-700">
                                      {item.price > 0 ? `$${item.price.toFixed(2)}` : 'TBD'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveItem(item.id)}
                                className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove item"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            
                            {/* Measurement Details - Integrated */}
                            {item.measurement && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                <div className="flex items-start gap-2.5">
                                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                                    <Ruler className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className="text-xs font-semibold text-blue-900">{item.measurement.name}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-blue-700">
                                      {item.measurement.length && (
                                        <span className="font-medium">L: <span className="text-blue-600">{item.measurement.length} {getStandardFieldUnit(item.measurement, 'length')}</span></span>
                                      )}
                                      {item.measurement.width && (
                                        <span className="font-medium">W: <span className="text-blue-600">{item.measurement.width} {getStandardFieldUnit(item.measurement, 'width')}</span></span>
                                      )}
                                      {item.measurement.height && (
                                        <span className="font-medium">H: <span className="text-blue-600">{item.measurement.height} {getStandardFieldUnit(item.measurement, 'height')}</span></span>
                                      )}
                                      {item.measurement.area && (
                                        <span className="font-medium">Area: <span className="text-blue-600">{item.measurement.area} {getStandardFieldUnit(item.measurement, 'area')}²</span></span>
                                      )}
                                      {item.measurement.custom_fields && Object.keys(item.measurement.custom_fields).length > 0 && (
                                        <>
                                          {Object.entries(item.measurement.custom_fields).map(([key, value]) => {
                                            const displayValue = getCustomFieldValue(value);
                                            const unit = getFieldUnit(value, getCustomFieldUnit(item.measurement, key));
                                            return (
                                              <span key={key} className="font-medium">
                                                {key}: <span className="text-blue-600">{displayValue}{unit ? ` ${unit}` : ''}</span>
                                              </span>
                                            );
                                          })}
                                        </>
                                      )}
                                    </div>
                                    {item.measurement.notes && (
                                      <p className="mt-2 text-xs text-blue-700 italic line-clamp-2">{item.measurement.notes}</p>
                                    )}
                                    {/* Measurement Photos */}
                                    {item.measurement_id && measurementPhotos[item.measurement_id] && measurementPhotos[item.measurement_id].length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-blue-100">
                                        <div className="flex items-center gap-1.5 mb-2">
                                          <ImageIcon className="h-3 w-3 text-blue-600" />
                                          <span className="text-xs font-medium text-blue-700">
                                            Photos ({measurementPhotos[item.measurement_id].length})
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          {measurementPhotos[item.measurement_id].slice(0, 3).map((photo) => {
                                            const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                                            return (
                                              <div
                                                key={photo.id}
                                                className="relative w-12 h-12 rounded overflow-hidden bg-gray-100 border border-blue-200 flex-shrink-0"
                                              >
                                                <img
                                                  src={photoUrl}
                                                  alt={photo.file_name}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                                                  }}
                                                />
                                              </div>
                                            );
                                          })}
                                          {measurementPhotos[item.measurement_id].length > 3 && (
                                            <span className="text-[10px] text-blue-600 font-medium">
                                              .....
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Item Button - Show when no measurement is selected and we're in measurements flow */}
                {!selectedMeasurementId && items.length > 0 && isQuoteFromMeasurements && (
                  <div className="mb-6">
                    <button
                      onClick={() => setCurrentStep('select-measurements')}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Plus className="h-4 w-4" />
                      Add Item
                    </button>
                  </div>
                )}

                {/* Item Form - Only show when a measurement is selected (for measurements flow) or always (for regular flow) */}
                {(!isQuoteFromMeasurements || selectedMeasurementId) && (
                  <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleSaveItem(e); }} noValidate>
                    <div className="space-y-4">
                      {/* Item Name */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Item Name *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Enter item name"
                          className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          required
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Description
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Enter item description (optional)"
                          rows={3}
                          className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all"
                        />
                      </div>

                      {/* Pricing Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Item Cost */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Estimated Cost *
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.acquisition_cost}
                              onChange={(e) => {
                                const cost = e.target.value;
                                setFormData(prev => {
                                  const shouldUpdatePrice = !prev.price || priceWasAutoFilledRef.current;
                                  return {
                                    ...prev,
                                    acquisition_cost: cost,
                                    price: shouldUpdatePrice ? cost : prev.price
                                  };
                                });
                                if (!formData.price) {
                                  priceWasAutoFilledRef.current = true;
                                }
                              }}
                              placeholder="0.00"
                              className="w-full pl-10 pr-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                              required
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Can be 0 if unknown</p>
                        </div>

                        {/* Retail Price */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Retail Price *
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.price}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, price: e.target.value }));
                                priceWasAutoFilledRef.current = false;
                              }}
                              placeholder="0.00"
                              className="w-full pl-10 pr-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                              required
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Can be 0 if unknown</p>
                        </div>
                      </div>

                      {/* Quantity */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Quantity *
                        </label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={formData.quantity}
                          onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                          placeholder="1"
                          className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          required
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 active:bg-gray-300 transition-colors duration-150 active:scale-[0.98]"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 active:scale-[0.98]"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Continue to Summary Button - Show when items exist and no measurement is selected (after saving) */}
                {items.length > 0 && !selectedMeasurementId && (
                  <div className="pt-4 border-t border-gray-200 mt-4">
                    <button
                      onClick={handleContinueToSummary}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 active:bg-green-800 transition-colors duration-150 active:scale-[0.98]"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Continue to Summary ({items.length} item{items.length > 1 ? 's' : ''})
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Summary Step */
              <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4">
                    {error}
                  </div>
                )}

                {/* Summary Content */}
                <div className="space-y-6">
                  {/* Selected Suppliers */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Recipients ({selectedSuppliers.length})</h3>
                    <div className="space-y-2">
                      {selectedSuppliers.map((supplier) => (
                        <div key={supplier.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-xs font-bold text-white">
                            {supplier.first_name?.[0]}{supplier.last_name?.[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {supplier.first_name} {supplier.last_name}
                            </div>
                            {supplier.email && (
                              <div className="text-xs text-gray-500 truncate">
                                {supplier.email}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Items Summary */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Items ({items.length})</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {items.map((item, index) => (
                        <div key={item.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                          <div className="p-4">
                            {/* Item Header */}
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                    {index + 1}
                                  </span>
                                  <h4 className="text-sm font-semibold text-gray-900">{item.name}</h4>
                                </div>
                                {item.description && (
                                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-gray-500">Qty:</span>
                                    <span className="font-medium text-gray-700">{item.quantity}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-gray-500">Cost:</span>
                                    <span className="font-medium text-gray-700">
                                      {item.acquisition_cost > 0 ? `$${item.acquisition_cost.toFixed(2)}` : 'TBD'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-gray-500">Price:</span>
                                    <span className="font-medium text-gray-700">
                                      {item.price > 0 ? `$${item.price.toFixed(2)}` : 'TBD'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Measurement Details */}
                            {item.measurement && (
                              <div className="pt-3 mt-3 border-t border-gray-100">
                                <div className="flex items-start gap-2.5">
                                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                                    <Ruler className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className="text-xs font-semibold text-blue-900">{item.measurement.name}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-blue-700">
                                      {item.measurement.length && (
                                        <span className="font-medium">L: <span className="text-blue-600">{item.measurement.length} {getStandardFieldUnit(item.measurement, 'length')}</span></span>
                                      )}
                                      {item.measurement.width && (
                                        <span className="font-medium">W: <span className="text-blue-600">{item.measurement.width} {getStandardFieldUnit(item.measurement, 'width')}</span></span>
                                      )}
                                      {item.measurement.height && (
                                        <span className="font-medium">H: <span className="text-blue-600">{item.measurement.height} {getStandardFieldUnit(item.measurement, 'height')}</span></span>
                                      )}
                                      {item.measurement.area && (
                                        <span className="font-medium">Area: <span className="text-blue-600">{item.measurement.area} {getStandardFieldUnit(item.measurement, 'area')}²</span></span>
                                      )}
                                      {item.measurement.custom_fields && Object.keys(item.measurement.custom_fields).length > 0 && (
                                        <>
                                          {Object.entries(item.measurement.custom_fields).map(([key, value]) => {
                                            const displayValue = getCustomFieldValue(value);
                                            const unit = getFieldUnit(value, getCustomFieldUnit(item.measurement, key));
                                            return (
                                              <span key={key} className="font-medium">
                                                {key}: <span className="text-blue-600">{displayValue}{unit ? ` ${unit}` : ''}</span>
                                              </span>
                                            );
                                          })}
                                        </>
                                      )}
                                    </div>
                                    {item.measurement.notes && (
                                      <p className="mt-2 text-xs text-blue-700 italic line-clamp-2">{item.measurement.notes}</p>
                                    )}
                                    {/* Measurement Photos */}
                                    {item.measurement_id && measurementPhotos[item.measurement_id] && measurementPhotos[item.measurement_id].length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-blue-100">
                                        <div className="flex items-center gap-1.5 mb-2">
                                          <ImageIcon className="h-3 w-3 text-blue-600" />
                                          <span className="text-xs font-medium text-blue-700">
                                            Photos ({measurementPhotos[item.measurement_id].length})
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          {measurementPhotos[item.measurement_id].slice(0, 3).map((photo) => {
                                            const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                                            return (
                                              <div
                                                key={photo.id}
                                                className="relative w-12 h-12 rounded overflow-hidden bg-gray-100 border border-blue-200 flex-shrink-0"
                                              >
                                                <img
                                                  src={photoUrl}
                                                  alt={photo.file_name}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                                                  }}
                                                />
                                              </div>
                                            );
                                          })}
                                          {measurementPhotos[item.measurement_id].length > 3 && (
                                            <span className="text-[10px] text-blue-600 font-medium">
                                              .....
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentStep('item-details')}
                      className="mt-4 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <Edit2 className="h-4 w-4" />
                      Add/Edit Items
                    </button>
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Additional Notes</h3>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any additional information for the supplier (optional)"
                      rows={3}
                      className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                  </div>

                  {/* Email Preview */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Email Preview</h3>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 max-h-64 overflow-y-auto">
                      <div 
                        className="text-sm text-gray-700 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: generateQuoteRequestEmailHTMLForMultipleItems(
                            selectedSuppliers[0]?.first_name || 'Supplier',
                            selectedBusiness?.name || 'Our Business',
                            projectTitle || null,
                            items.map(item => {
                              const measurement = resolveMeasurementForItem(item);
                              return ({
                                name: item.name,
                                description: item.description || null,
                                quantity: item.quantity,
                                estimatedCost: item.acquisition_cost,
                                retailPrice: item.price,
                                unit: 'unit',
                                measurement: measurement ? {
                                  name: measurement.name,
                                  length: measurement.length || null,
                                  width: measurement.width || null,
                                  height: measurement.height || null,
                                  area: measurement.area || null,
                                  unit: measurement.unit,
                                  custom_fields: measurement.custom_fields || null,
                                  field_units: buildMeasurementFieldUnits(measurement) || null,
                                  photos: item.measurement_id && measurementPhotos[item.measurement_id] 
                                    ? measurementPhotos[item.measurement_id].map(photo => ({
                                        file_path: photo.file_path,
                                        file_name: photo.file_name
                                      }))
                                    : null
                                } : null
                              });
                            }),
                            notes.trim() || null
                          )
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Each supplier will receive a separate email with all items listed
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-6 mt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 active:bg-gray-300 transition-colors duration-150 active:scale-[0.98]"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSendQuoteRequests}
                    disabled={loading}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        <span>Send to {selectedSuppliers.length} Supplier{selectedSuppliers.length > 1 ? 's' : ''}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

